import { z } from 'zod'

/** POST /photo-frames レスポンス内 フレームレイヤーデータ（生 API スキーマ） */
export const PhotoFrameItemDataSchema = z.object({
  file: z.string().nullable(),
  credit: z.boolean(),
  magnification: z.number(),
  movable: z.boolean(),
  x: z.number().nullable(),
  y: z.number().nullable(),
})
export type PhotoFrameItemData = z.infer<typeof PhotoFrameItemDataSchema>

/**
 * status が out_of_range のときの内訳（#3562）。
 * location_required = 座標が送られてこなかった（未許可・取得失敗・未測位）。too_far = 実距離が範囲を超えている。
 * 古いレスポンスに備えて欠落時は null に倒す。
 */
export const LocationReasonSchema = z.enum(['location_required', 'too_far']).nullable()
export type LocationReason = z.infer<typeof LocationReasonSchema>

/** POST /photo-frames レスポンス内 エントリ（生 API スキーマ） */
export const PhotoFrameEntryDataSchema = z.object({
  id: z.number(),
  status: z.enum(['available', 'stamp_required', 'out_of_range']),
  reason: LocationReasonSchema.default(null),
  message: z.string().nullable(),
  /**
   * AR起動時に再生する音声ファイルURL（#3261）。音声未設定・利用不可時は null。
   * プライベート署名付きURL（有効期限約60分）のためキャッシュせず、AR起動直前に再取得すること。
   */
  audio: z.string().nullable(),
  item: z.array(PhotoFrameItemDataSchema).nullable(),
})
export type PhotoFrameEntryData = z.infer<typeof PhotoFrameEntryDataSchema>

/** POST /photo-frames レスポンス（生 API スキーマ） */
export const PhotoFrameResponseSchema = z.object({
  photoFrames: z.array(PhotoFrameEntryDataSchema),
})
export type PhotoFrameResponse = z.infer<typeof PhotoFrameResponseSchema>

/** ArPhotoOverlay コンポーネントに渡すフレームデータ（DTO） */
export const PhotoFrameItemSchema = z.object({
  file: z
    .string()
    .describe(
      'フレーム画像URL。プライベート保存分は S3 署名付きURL（有効期限約60分）、旧公開分は CloudFront URL。' +
        'クエリ文字列に署名を含むため、キャッシュバスター等を後から連結しないこと（#3372）',
    ),
  fit: z.boolean(),
  magnification: z.number().describe('表示倍率（パーセンテージ: 100 = 等倍）'),
  credit: z.boolean(),
})
export type PhotoFrameItem = z.infer<typeof PhotoFrameItemSchema>

/**
 * POST /photo-frames レスポンスを ArPhotoOverlay 用 PhotoFrameItem[] に変換する。
 * status が 'available' かつ item が存在し file が non-null のものを変換する。
 * movable=false → fit=true（固定オーバーレイ）
 * magnification: v2 API は float（1.0 = 等倍、1.5 = 150% など）で返す。
 *   calculateCanvasDimensions はパーセンテージ表記（100 = 等倍）を期待するため * 100 で変換する。
 */
/**
 * POST /photo-frames レスポンスから AR起動時に自動再生する音声URLを取り出す（#3261）。
 * status が 'available' かつ audio が non-null の最初の entry の audio を返す。無ければ null。
 * ⚠️ 署名付きURL（約60分）のためキャッシュせず、AR起動直前の取得結果からその都度取り出すこと。
 */
export const pickPhotoFrameAudio = (entries: PhotoFrameEntryData[]): string | null =>
  entries.find((e) => e.status === 'available' && e.audio != null && e.audio !== '')?.audio ?? null

export const toPhotoFrameItems = (entries: PhotoFrameEntryData[]): PhotoFrameItem[] =>
  entries
    .filter((e) => e.status === 'available' && e.item != null)
    .flatMap((e) => e.item ?? [])
    .filter((item): item is PhotoFrameItemData & { file: string } => item.file !== null && item.file !== '')
    .map((item) => ({
      file: item.file,
      fit: !item.movable,
      magnification: item.magnification * 100,
      credit: item.credit,
    }))
